import { useState } from 'react'

import { Box, Button, Divider, useToggle } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import { canApproveCard } from 'modules/banking/lib/cards/canApproveCard'
import { canCancelCard } from 'modules/banking/lib/cards/canCancelCard'
import { canDeactivateCard } from 'modules/banking/lib/cards/canDeactivateCard'
import { canReactivateCard } from 'modules/banking/lib/cards/canReactivateCard'
import { last4digits } from 'modules/banking/lib/cards/last4digits'

import { CardSidepanel } from 'modules/banking/components/CardSidepanel'
import { ApproveModal } from 'modules/banking/components/CardSidepanel/_Content/_ApproveModal'
import { CancelModal } from 'modules/banking/components/CardSidepanel/_Content/_CancelModal'
import { DeactivateModal } from 'modules/banking/components/CardSidepanel/_Content/_DeactivateModal'
import { ReactivateModal } from 'modules/banking/components/CardSidepanel/_Content/_ReactivateModal'
import { RejectModal } from 'modules/banking/components/CardSidepanel/_Content/_RejectModal'
import { Card } from './types'

interface CardActionsProps {
  card: Card
  bankAccountId?: string | undefined
}

interface PopoverProps {
  card: Card
  toggleDeactivateModal: (value: boolean) => () => void
  toggleCancelModal: (value: boolean) => () => void
  toggleReactivateModal: (value: boolean) => () => void
  toggleApproveModal: (value: boolean) => () => void
  toggleRejectModal: (value: boolean) => () => void
}

const Popover = ({
  card,
  toggleDeactivateModal,
  toggleCancelModal,
  toggleReactivateModal,
  toggleApproveModal,
  toggleRejectModal
}: PopoverProps) => {
  const statusAction = () => {
    switch (card.status) {
      case 'requested':
        return (
          canApproveCard(card) && (
            <>
              <PopoverMenu.TouchableOption
                onClick={toggleApproveModal(true)}
                title={i18n.t('expenses_v2.cards.actions.approve')}
              />
              <Divider></Divider>
              <PopoverMenu.TouchableOption
                onClick={toggleRejectModal(true)}
                title={i18n.t('expenses_v2.cards.actions.reject')}
                danger
              />
            </>
          )
        )
      case 'inactive':
        return (
          canReactivateCard(card) && (
            <>
              <PopoverMenu.TouchableOption
                onClick={toggleReactivateModal(true)}
                title={i18n.t('expenses_v2.cards.actions.reactivate')}
              />
              <Divider></Divider>
            </>
          )
        )
      case 'active':
        return (
          canDeactivateCard(card) && (
            <>
              <PopoverMenu.TouchableOption
                onClick={toggleDeactivateModal(true)}
                title={i18n.t('expenses_v2.cards.actions.deactivate')}
              />
              <Divider></Divider>
            </>
          )
        )
      default:
        return <></>
    }
  }

  const actions = () => {
    return (
      <>
        {statusAction()}
        {canCancelCard(card) && (
          <PopoverMenu.TouchableOption
            onClick={toggleCancelModal(true)}
            title={i18n.t('expenses_v2.cards.actions.cancel')}
            danger
          />
        )}
      </>
    )
  }

  if (card.status === 'canceled') {
    return <Button type="secondary" onClick={() => true} disabled iconName="ellipsis" />
  }

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="end">
        {actions()}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export const Actions = ({ card }: CardActionsProps) => {
  const [showDeactivateModal, toggleDeactivateModal] = useToggle(false)
  const [showCancelModal, toggleCancelModal] = useToggle(false)
  const [showReactivateModal, toggleReactivateModal] = useToggle(false)
  const [showApproveModal, toggleApproveModal] = useToggle(false)
  const [showRejectModal, toggleRejectModal] = useToggle(false)
  const [showCardSidePanel, setShowCardsSidePanel] = useState(false)

  return (
    <>
      <Box gap="s4" flexDirection="row" justifyContent="flexEnd">
        <Popover
          card={card}
          toggleDeactivateModal={toggleDeactivateModal}
          toggleCancelModal={toggleCancelModal}
          toggleReactivateModal={toggleReactivateModal}
          toggleRejectModal={toggleRejectModal}
          toggleApproveModal={toggleApproveModal}
        />
        <Button
          aria-label={i18n.t('expenses_v2.list.actions.see_card_details', {
            last4: last4digits(card)
          })}
          onClick={() => setShowCardsSidePanel(true)}
          type="secondary"
          iconName="arrow-right"
        />
      </Box>
      {showDeactivateModal && (
        <DeactivateModal card={card} onClose={toggleDeactivateModal(false)} />
      )}
      {showReactivateModal && (
        <ReactivateModal card={card} onClose={toggleReactivateModal(false)} />
      )}
      {showCancelModal && <CancelModal card={card} onClose={toggleCancelModal(false)} />}

      {showApproveModal && <ApproveModal card={card} onClose={toggleApproveModal(false)} />}
      {showRejectModal && <RejectModal card={card} onClose={toggleRejectModal(false)} />}
      {showCardSidePanel && (
        <CardSidepanel cardId={card.id} onClose={() => setShowCardsSidePanel(false)} />
      )}
    </>
  )
}
